import { Link } from 'react-router-dom';
import { useOrders } from '../../api/orders';
import { EmptyState, ErrorBox, LoadingBlock, Panel } from '../../components/ui/feedback';
import { MailStatusPill } from '../../components/ui/status-pill';
import { formatShortDate, plural } from '../../lib/format';
import pageStyles from '../../components/layout/page.module.css';
import styles from './projects.module.css';

/** Enough to answer "what has been ordered here lately"; the rest is one click away. */
const PREVIEW_SIZE = 5;

interface ProjectOrdersProps {
  projectId: string;
  from: string | undefined;
  to: string | undefined;
}

/**
 * What was ordered for this site.
 *
 * The other half of the manager's question about a build: the invoices panel
 * says what has already been billed, this one what is on its way. Reads the
 * same period as the rest of the screen, so both lists describe one window.
 */
export function ProjectOrders({ projectId, from, to }: ProjectOrdersProps) {
  const { data, isLoading, error } = useOrders({
    projectId,
    placedFrom: from ? new Date(from) : undefined,
    placedTo: to ? new Date(to) : undefined,
    pageSize: PREVIEW_SIZE,
  });

  const orders = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <Panel
      title={`Zamówienia — ${total} ${plural(total, ['zamówienie', 'zamówienia', 'zamówień'])}`}
      action={
        total > 0 ? (
          <Link className={styles.panelLink} to={`/zamowienia?projectId=${projectId}`}>
            Otwórz w historii →
          </Link>
        ) : undefined
      }
    >
      {error && <ErrorBox>Nie udało się pobrać zamówień tej budowy.</ErrorBox>}
      {isLoading && <LoadingBlock />}

      {!isLoading && !error && orders.length === 0 && (
        <EmptyState
          mark="∅"
          title="Brak zamówień"
          description="Na tej budowie nikt nie złożył zamówienia w wybranym okresie."
        />
      )}

      {orders.length > 0 && (
        <div className={pageStyles.tableWrap}>
          <table className={pageStyles.table}>
            <thead>
              <tr>
                <th>Kiedy</th>
                <th>Do kogo</th>
                <th className={pageStyles.hideOnPhone}>Złożył</th>
                <th className={pageStyles.numeric}>Pozycji</th>
                <th>Wysyłka</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className={pageStyles.muted}>{formatShortDate(order.placedAt)}</td>
                  <td className={pageStyles.strong}>{order.vendorName ?? order.recipientEmail}</td>
                  <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>
                    {order.placedByName}
                  </td>
                  <td className={pageStyles.numeric}>{order.items.length}</td>
                  <td>
                    <MailStatusPill status={order.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
